/* eslint-disable @typescript-eslint/no-explicit-any */
import {FC, useContext} from 'react';
import {Catalog} from 'models/catalog';
import {LEAVE_STATUS_DRAFT, LeaveRequest} from 'models/leaveRequest';
import Modal from 'components/Modal/Modal';
import GqlFormController from 'controllers/GqlFormController/GqlFormController';
import {Button, ModalProps, Typography} from '@mui/material';
import {useTranslation} from 'react-i18next';
import {UPDATE_LEAVE_REQUEST} from 'graphql/absences/Mutations';
import dayjs from 'dayjs';
import GqlQueryController from 'controllers/GqlQueryController/GqlQueryController';
import {GET_CATALOGS} from 'graphql/absences/Querys';
import LeaveRequestEvidenceController from 'controllers/LeaveRequestEvidenceController';
import {AssignationType} from 'models/assignationType';
import withFormViewModel from 'viewComponents/CreateLeaveRequestForm/formViewModel';
import {formatDate} from 'commons/dates';
import './LeaveRequestUpdateModal.scss';
import {END_AT_MORNING, START_AT_EVENING} from 'models/halfDay';
import {ADDITIONAL_LEAVE} from 'models/leaveRequestType';
import {authContext} from 'contextApi/context/auth';
import {alertContext} from 'contextApi/context/error';
import {useTranslateCatalog} from 'commons/hooks/useTranslateCatalog';

interface LeaveRequestUpdateModalProps
  extends Omit<ModalProps, 'children' | 'onSubmit'> {
  children?: Children;
  leaveData?: LeaveRequest;
  onSaveSuccess?: () => void;
  onSubmit: (mutation: any, data: any) => Promise<any>;
}

const LeaveRequestUpdateModal: FC<LeaveRequestUpdateModalProps> = ({
  onClose,
  leaveData,
  onSaveSuccess,
  onSubmit: formViewModelSubmit,
  ...other
}) => {
  const {userDetails} = useContext(authContext);
  const {dispatch} = useContext(alertContext);
  const {t} = useTranslation();

  const defaultValues = {
    id: leaveData?.id,
    leaveRangeDate: {
      startDate: dayjs(leaveData?.startDate).toDate(),
      endDate: dayjs(leaveData?.endDate).toDate(),
      startCheck: leaveData?.scheduleOptions?.find(
        value => value === START_AT_EVENING
      ),
      endCheck: leaveData?.scheduleOptions?.find(
        value => value === END_AT_MORNING
      ),
    },
  };

  const handleOnSubmit = async (mutationPromise: any, data: any) => {
    try {
      // Usar la función submit del formViewModel que maneja la subida de archivos
      await formViewModelSubmit(mutationPromise, data);

      dispatch({
        type: 'show',
        payload: {
          level: 'success',
          message: t('leaves.LeaveRequestUpdateModal.successMessage'),
          duration: 'short',
        },
      });

      // Cerrar el modal
      onClose && onClose({} as any, 'backdropClick');

      // Actualizar datos en el componente padre
      onSaveSuccess && onSaveSuccess();
    } catch (error: any) {
      console.error('Error updating leave request:', error);
      dispatch({
        type: 'show',
        payload: {
          message:
            error?.message || t('leaves.LeaveRequestUpdateModal.errorMessage'),
          level: 'error',
        },
      });
    }
  };

  const variablesMapper = (formData: any) => {
    const scheduleOptions: string[] = [];
    formData.leaveRangeDate.startCheck &&
      scheduleOptions.push(START_AT_EVENING);
    formData.leaveRangeDate.endCheck && scheduleOptions.push(END_AT_MORNING);

    const request = {
      leaveRequest: {
        id: leaveData?.id,
        startDate: formatDate(formData.leaveRangeDate.startDate),
        endDate: formatDate(formData.leaveRangeDate.endDate),
        scheduleOptions,
        isDraft: leaveData?.status === LEAVE_STATUS_DRAFT,
      },
    };
    return request;
  };

  return (
    <Modal
      title={t('leaves.LeaveRequestUpdateModal.title')}
      description={t('leaves.LeaveRequestUpdateModal.description')}
      onClose={onClose}
      {...other}
    >
      <GqlQueryController
        QUERY={GET_CATALOGS}
        variables={{type: 'CHANGE_REASON', country: userDetails?.data?.country}}
        initialState={[] as Catalog[]}
        rootField="catalogs"
      >
        {({data: moditicationTypes}) => (
          <UpdateFormContent
            moditicationTypes={moditicationTypes}
            leaveData={leaveData}
            onClose={onClose}
            handleOnSubmit={handleOnSubmit}
            variablesMapper={variablesMapper}
            defaultValues={defaultValues}
            t={t}
          />
        )}
      </GqlQueryController>
    </Modal>
  );
};

// Inner component to handle catalog translation
const UpdateFormContent: FC<any> = ({
  moditicationTypes,
  leaveData,
  onClose,
  handleOnSubmit,
  variablesMapper,
  defaultValues,
  t,
}) => {
  const {translateCatalogs} = useTranslateCatalog();

  // Translate catalogs directly without state to avoid infinite loops
  const translatedModificationTypes = translateCatalogs(moditicationTypes);

  return (
    <LeaveRequestEvidenceController
      assignationType={AssignationType.PROJECT}
      leaveType={leaveData?.typeOfLeave?.name}
    >
      {({evidenceFormControls}) => (
        <GqlFormController
          mutation={UPDATE_LEAVE_REQUEST}
          variablesMapper={variablesMapper}
          className="flex flex-col"
          itemProps={{xs: 12}}
          defaultValues={{...defaultValues}}
          onSubmit={handleOnSubmit}
          controls={[
            {
              name: 'typeOfLeave',
              type: 'label',
              props: {
                label: t('leaves.LeaveRequestUpdateModal.typeOfLeave'),
                value: leaveData?.typeOfLeave?.description,
              },
            },
            {
              name: 'reason',
              type: 'select',
              options: {
                required: true,
              },
              props: {
                label: 'Motivo de modificación',
                placeholder: 'Selecciona una opción',
                className: 'mt-2',
                noSelection: t('common.choose_an_option'),
                children: (translatedModificationTypes || []).map(
                  (option: any) => ({
                    key: option.id,
                    description: option.description,
                  })
                ),
              },
            },
            {
              name: 'leaveRangeDate',
              type: 'leaveDateRangePicker',
              options: {
                required: true,
              },
              props: {
                labels: {
                  startDateLabel: t(
                    'leaves.MyLeaves.inputLabels.startDateLabel'
                  ),
                  endDateLabel: t('leaves.MyLeaves.inputLabels.endDateLabel'),
                  startCheckLabel: t(
                    'leaves.MyLeaves.inputLabels.startCheckLabel'
                  ),
                  endCheckLabel: t('leaves.MyLeaves.inputLabels.endCheckLabel'),
                  optionalStartCheckLabel: t(
                    'leaves.MyLeaves.inputLabels.optionalStartCheckLabel'
                  ),
                  optionalEndCheckLabel: t(
                    'leaves.MyLeaves.inputLabels.optionalEndCheckLabel'
                  ),
                },
                withChecks: leaveData?.typeOfLeave.name !== ADDITIONAL_LEAVE,
              },
            },
            {
              name: 'newEvidences',
              type: 'label',
              props: {
                value: t('leaves.LeaveRequestUpdateModal.newEvidences'),
              },
            },
            evidenceFormControls,
          ]}
        >
          {({onSubmit}: any) => (
            <div>
              {/* Inform the user which fields are mandatory before submitting */}
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{display: 'block', mb: 1}}
              >
                {t('leaves.required_fields_legend')}
              </Typography>
              <Button
                variant="outlined"
                onClick={event => onClose && onClose(event, 'backdropClick')}
              >
                {t('leaves.LeaveRequestUpdateModal.exit')}
              </Button>
              <Button onClick={onSubmit()} variant="contained">
                {t('leaves.LeaveRequestUpdateModal.submit')}
              </Button>
            </div>
          )}
        </GqlFormController>
      )}
    </LeaveRequestEvidenceController>
  );
};

export default withFormViewModel()(LeaveRequestUpdateModal);
